import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { dateFormats } from 'commons/src/constants';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { BuildingType } from 'commons/src/models/commonTypeScript';
import { IaqInsightData, PDFContext, ReportSensorThresholds } from '../../../../models/common';
import IaqInsight2BuildingDetails from './components/IaqInsight2BuildingDetails';
import IaqInsight2Footer from './components/IaqInsight2Footer';
import IaqInsight2Header from './components/IaqInsight2Header';
import IaqInsight2ReportDetails from './components/IaqInsight2ReportDetails';
import IaqInsight2SensorSection from './components/IaqInsight2SensorSection';
import IaqInsight2SummaryCards from './components/IaqInsight2SummaryCards';
import { SpaceMeasurementDataColumns } from './utils/pdfHelpers';
import iaqInsight2PdfStyles from './utils/pdfStyles';

type IaqInsight2PdfDocumentProps = {
    building: BuildingType;
    spaceMeasurementColumnsBySensorType: { [sensor: string]: SpaceMeasurementDataColumns };
    translationFunction: PDFContext;
    dateFormat: keyof typeof dateFormats;
    reports: { [sensor: string]: IaqInsightData };
    thresholds: { [sensor: string]: ReportSensorThresholds };
    reportDetails: {
        deviceTypes: string[];
        userName: string;
        reportId: string;
    };
    deviceTypesInReport: string[];
    comment: string;
    listOfImages: { data: string; serialNumber: string }[];
    fromDate: string | null;
    toDate: string | null;
    hideCharts?: boolean;
    logo?: string;
};

const IaqInsight2PdfDocument = React.memo(
    ({
        building,
        spaceMeasurementColumnsBySensorType,
        translationFunction,
        dateFormat,
        reports,
        thresholds,
        reportDetails,
        deviceTypesInReport,
        comment,
        listOfImages,
        fromDate,
        toDate,
        hideCharts,
        logo,
    }: IaqInsight2PdfDocumentProps): React.ReactElement => {
        // Define custom sensor order for IAQInsight2: CO2, Temp, Humidity, VOC, PM2.5, PM1, Light, Noise
        const sensorOrder = [
            SensorTypes.co2,
            SensorTypes.temp,
            SensorTypes.humidity,
            SensorTypes.voc,
            SensorTypes.pm25,
            SensorTypes.pm1,
            SensorTypes.light,
            SensorTypes.noise,
        ];
        const sensorOrderMap = new Map(sensorOrder.map((sensor, index) => [sensor, index]));
        const sortedSensorTypeKeys = Object.keys(reports).sort((sensorA, sensorB) => {
            const indexA = sensorOrderMap.get(sensorA as SensorTypes) ?? 999;
            const indexB = sensorOrderMap.get(sensorB as SensorTypes) ?? 999;
            return indexA - indexB;
        });

        return (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            <Document>
                <Page size="A4" style={iaqInsight2PdfStyles.page}>
                    <IaqInsight2Header
                        building={building}
                        translationFunction={translationFunction}
                        fromDate={fromDate}
                        toDate={toDate}
                        logo={logo}
                    />
                    <IaqInsight2BuildingDetails
                        translationFunction={translationFunction}
                        building={building}
                        dateFormat={dateFormat}
                    />
                    <View style={iaqInsight2PdfStyles.pageLine} />
                    <Text style={iaqInsight2PdfStyles.sectionTitle}>
                        {translationFunction('IAQReport2.AirQualityOverview')}
                    </Text>
                    <Text style={iaqInsight2PdfStyles.airQualityAveragesDescription}>
                        {translationFunction('IAQReport2.AirQualityAveragesDescription')}
                    </Text>
                    <IaqInsight2SummaryCards
                        translationFunction={translationFunction}
                        reports={reports}
                        thresholds={thresholds}
                    />
                    <View style={iaqInsight2PdfStyles.pageLine} />
                    {sortedSensorTypeKeys.map((sensorTypeKey, index) => {
                        return (
                            <React.Fragment key={`sensor-${sensorTypeKey}`}>
                                <View break />
                                {index === 0 && (
                                    <Text style={iaqInsight2PdfStyles.sectionTitle}>
                                        {translationFunction('IAQReport2.AirQualityDetails')}
                                    </Text>
                                )}
                                <IaqInsight2SensorSection
                                    sensorType={sensorTypeKey}
                                    translationFunction={translationFunction}
                                    sensorReportData={reports[sensorTypeKey]}
                                    sensorThresholdConfiguration={thresholds[sensorTypeKey]}
                                    spaceMeasurementColumns={spaceMeasurementColumnsBySensorType[sensorTypeKey]}
                                    listOfImages={listOfImages}
                                    hideCharts={hideCharts}
                                />
                            </React.Fragment>
                        );
                    })}
                    <IaqInsight2ReportDetails
                        userName={reportDetails.userName}
                        dateFormat={dateFormat}
                        translationFunction={translationFunction}
                        reportId={reportDetails.reportId}
                        deviceTypes={deviceTypesInReport}
                        comment={comment}
                    />
                    <IaqInsight2Footer />
                </Page>
            </Document>
        );
    }
);

export default IaqInsight2PdfDocument;
